"use client";

import {
  Badge,
  cn,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  ChevronRightIcon,
  ShoppingCartIcon,
  StoreIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  WalletIcon,
  WrenchIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatPrice } from "@/shared/lib/format-price";
import { BarChart } from "@/shared/ui/BarChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import type { Period } from "../api/client";
import { useDashboardAnalyticsQuery } from "../api/queries";

const PERIODS: { value: Period; label: string }[] = [
  { value: "day", label: "ວັນ" },
  { value: "week", label: "ອາທິດ" },
  { value: "month", label: "ເດືອນ" },
];

const DAY_LABELS_LAO: Record<string, string> = {
  MON: "ຈັນ",
  TUE: "ອັງຄານ",
  WED: "ພຸດ",
  THU: "ພະຫັດ",
  FRI: "ສຸກ",
  SAT: "ເສົາ",
  SUN: "ອາທິດ",
};

function formatChartLabel(label: string, period: Period): string {
  if (period === "day") {
    return `${label}:00`;
  }
  if (period === "month") {
    return `ວັນທີ ${Number.parseInt(label, 10)}`;
  }
  return DAY_LABELS_LAO[label] ?? label;
}

const CHART_TITLE: Record<Period, string> = {
  day: "ຄຳສັ່ງຕໍ່ຊົ່ວໂມງ",
  week: "ຄຳສັ່ງຕໍ່ວັນ",
  month: "ຄຳສັ່ງຕໍ່ວັນ",
};

const CHART_DESC: Record<Period, string> = {
  day: "ຈຳນວນທຸລະກຳແຕ່ລະຊົ່ວໂມງໃນມື້ນີ້",
  week: "ຈຳນວນທຸລະກຳປະຈຳວັນສຳລັບອາທິດນີ້",
  month: "ຈຳນວນທຸລະກຳປະຈຳວັນສຳລັບເດືອນນີ້",
};

const chartConfig: ChartConfig = {
  orders: { label: "ຄຳສັ່ງ", color: "var(--color-secondary)" },
};

type AlertItem = {
  id: string;
  type: "report" | "dispute";
  targetLabel: string;
  reason: string;
  createdAt: Date | null;
};

function formatTimeAgo(date: Date | null): string {
  if (!date) return "—";
  const d = date instanceof Date ? date : new Date(date);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "ຫາກໍ່ນີ້";
  if (mins < 60) return `${mins} ນາທີກ່ອນ`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ຊົ່ວໂມງກ່ອນ`;
  const days = Math.floor(hours / 24);
  return `${days} ວັນກ່ອນ`;
}

function ChangeIndicator({
  value,
  className,
}: {
  value: number;
  className?: string;
}) {
  if (value === 0) {
    return (
      <span
        className={cn(
          "flex items-center gap-1 text-muted-foreground text-xs",
          className,
        )}
      >
        ຄົງທີ່ —
      </span>
    );
  }
  const isPositive = value > 0;
  return (
    <span
      className={cn(
        "flex items-center gap-1 text-xs",
        isPositive ? "text-success" : "text-destructive",
        className,
      )}
    >
      {isPositive ? (
        <TrendingUpIcon className="size-3.5" />
      ) : (
        <TrendingDownIcon className="size-3.5" />
      )}
      {isPositive ? "+" : ""}
      {value}%
    </span>
  );
}

const TYPE_LABELS: Record<string, string> = {
  listing: "ລາຍການ",
  review: "ລີວິວ",
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງ",
  job: "ວຽກ",
  order: "ຄຳສັ່ງ",
};

export function DashboardPage() {
  const [period, setPeriod] = useState<Period>("week");
  const { data, isLoading } = useDashboardAnalyticsQuery(period);

  const d = data ?? {
    gmv: 0,
    gmvChangePercent: 0,
    marketplaceCommission: 0,
    marketplaceCommissionChangePercent: 0,
    totalOrders: 0,
    ordersChangePercent: 0,
    activeShops: 0,
    activeShopsLabel: "",
    activeMechanics: 0,
    activeMechanicsLabel: "",
    ordersTrend: [],
    moderationAlerts: {
      pendingReports: 0,
      openDisputes: 0,
      recentReports: [],
      recentDisputes: [],
    },
    reconciliationAlert: {
      hasMismatch: false,
      mismatchCount: 0,
      checkedDate: new Date().toISOString().slice(0, 10),
    },
  };

  const chartData =
    d.ordersTrend.length > 0
      ? d.ordersTrend.map((r) => ({
          label: formatChartLabel(r.label, period),
          orders: r.orders,
        }))
      : [{ label: "—", orders: 0 }];

  const totalPendingAlerts =
    d.moderationAlerts.pendingReports + d.moderationAlerts.openDisputes;

  const alertItems: AlertItem[] = [
    ...d.moderationAlerts.recentReports.map((r) => ({
      id: r.id,
      type: "report" as const,
      targetLabel: r.targetType
        ? `${TYPE_LABELS[r.targetType] ?? r.targetType}: ${r.targetId?.slice(0, 8) ?? "—"}`
        : "—",
      reason: r.reason ?? "—",
      createdAt: r.createdAt,
    })),
    ...d.moderationAlerts.recentDisputes.map((dp) => ({
      id: dp.id,
      type: "dispute" as const,
      targetLabel: dp.type
        ? `${TYPE_LABELS[dp.type] ?? dp.type}: ${dp.referenceId?.slice(0, 8) ?? "—"}`
        : "—",
      reason: dp.reason ?? "—",
      createdAt: dp.createdAt,
    })),
  ].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ວິເຄາະແພລດຟອມ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ແຜງວິເຄາະແພລດຟອມ
            </h2>
            <p className="text-muted-foreground">
              ພາບລວມ GMV, ຄຳສັ່ງ, ຮ້ານຄ້າ, ຊ່າງ ແລະ ການແຈ້ງເຕືອນ
            </p>
          </div>
          <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
            <TabsList className="rounded-lg border bg-muted p-1">
              {PERIODS.map(({ value, label }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="rounded-md px-4 py-2 font-medium text-sm data-[state=active]:shadow-sm"
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {isLoading ? (
          <DashboardSkeleton />
        ) : (
          <>
            {/* KPI Cards Row */}
            <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <SummaryCard
                title="GMV ລວມ"
                icon={WalletIcon}
                color="orange"
                items={[{ label: "", value: formatPrice(d.gmv, "LAK") }]}
                subtitle={<ChangeIndicator value={d.gmvChangePercent} />}
              />
              <SummaryCard
                title="ຄ່າຄອມ Marketplace"
                icon={WalletIcon}
                color="orange"
                items={[
                  {
                    label: "",
                    value: formatPrice(d.marketplaceCommission, "LAK"),
                  },
                ]}
                subtitle={
                  <ChangeIndicator
                    value={d.marketplaceCommissionChangePercent}
                  />
                }
              />
              <SummaryCard
                title="ຄຳສັ່ງ"
                icon={ShoppingCartIcon}
                color="primary"
                items={[{ label: "", value: d.totalOrders.toLocaleString() }]}
                subtitle={<ChangeIndicator value={d.ordersChangePercent} />}
              />
              <SummaryCard
                title="ຮ້ານທີ່ Active"
                icon={StoreIcon}
                color="primary"
                items={[{ label: "", value: d.activeShops.toLocaleString() }]}
                subtitle={<span className="text-muted-foreground">ຄົງທີ່ —</span>}
              />
              <SummaryCard
                title="ຊ່າງທີ່ Active"
                icon={WrenchIcon}
                color="primary"
                items={[
                  { label: "", value: d.activeMechanics.toLocaleString() },
                ]}
                subtitle={<span className="text-muted-foreground">ຄົງທີ່ —</span>}
              />
            </div>

            {/* Chart Section */}
            <div className="mb-6 rounded-xl border border-border bg-card p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-bold text-lg tracking-tight">
                    {CHART_TITLE[period]}
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    {CHART_DESC[period]}
                  </p>
                </div>
              </div>
              <div className="mt-4 h-[300px] w-full">
                <BarChart
                  data={chartData}
                  config={chartConfig}
                  xDataKey="label"
                  valueDataKey="orders"
                  className="h-full w-full"
                />
              </div>
            </div>

            {/* Moderation Alerts Section */}
            {d.reconciliationAlert.hasMismatch && (
              <div className="mb-6 rounded-xl border border-destructive/40 bg-destructive/5 p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangleIcon className="mt-0.5 size-5 text-destructive" />
                  <div>
                    <h3 className="font-semibold text-destructive">
                      Marketplace reconciliation mismatch
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      ພົບຄວາມບໍ່ກົງກັນ {d.reconciliationAlert.mismatchCount} ລາຍການ
                      ວັນທີ {d.reconciliationAlert.checkedDate} — ກວດ wallet vs
                      settlements
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex size-2 rounded-full bg-primary" />
                  <div>
                    <h2 className="font-bold text-lg tracking-tight">
                      ການແຈ້ງເຕືອນ
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      ລາຍການທີ່ຕ້ອງການດຳເນີນການ
                    </p>
                  </div>
                </div>
                {totalPendingAlerts > 0 && (
                  <Badge
                    variant="destructive"
                    className="rounded-full px-3 py-1"
                  >
                    {totalPendingAlerts} ລາຍການລໍຖ້າ
                  </Badge>
                )}
              </div>

              {alertItems.length > 0 ? (
                <div className="mt-4 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[140px]">ປະເພດ</TableHead>
                        <TableHead>ເປົ້າໝາຍ</TableHead>
                        <TableHead>ເຫດຜົນ</TableHead>
                        <TableHead className="w-[120px]">ເວລາ</TableHead>
                        <TableHead className="w-[120px] text-right">
                          ການດຳເນີນການ
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alertItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Badge
                              variant={
                                item.type === "report" ? "secondary" : "outline"
                              }
                              className={cn(
                                "rounded-full px-2.5 py-0.5 text-xs",
                                item.type === "report"
                                  ? "border-secondary/30 bg-secondary/10 text-secondary"
                                  : "border-primary/30 bg-primary/10 text-primary",
                              )}
                            >
                              {item.type === "report" ? "ລາຍງານ" : "ຂໍ້ພິພາດ"}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">
                            {item.targetLabel}
                          </TableCell>
                          <TableCell className="text-muted-foreground italic">
                            {item.reason}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatTimeAgo(item.createdAt)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Link
                              to={
                                item.type === "report"
                                  ? "/app/moderation/reports/$id"
                                  : "/app/disputes/$id"
                              }
                              params={{ id: item.id }}
                              className="inline-flex items-center gap-1 font-medium text-primary text-sm hover:underline"
                            >
                              ໄປຈັດການ
                              <ChevronRightIcon className="size-4" />
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="mt-6 flex flex-col items-center gap-2 py-8 text-center">
                  <AlertTriangleIcon className="size-8 text-muted-foreground/50" />
                  <p className="text-muted-foreground">ບໍ່ມີການແຈ້ງເຕືອນໃນຂະນະນີ້</p>
                </div>
              )}
            </div>
          </>
        )}
      </Main>
    </>
  );
}

function DashboardSkeleton() {
  return (
    <>
      {/* KPI Cards Skeleton */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[100px] rounded-xl" />
        ))}
      </div>

      {/* Chart Skeleton */}
      <div className="mb-6 rounded-xl border border-border bg-card p-5">
        <Skeleton className="mb-2 h-5 w-40" />
        <Skeleton className="mb-4 h-4 w-64" />
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </div>

      {/* Alerts Table Skeleton */}
      <div className="rounded-xl border border-border bg-card p-5">
        <Skeleton className="mb-4 h-5 w-32" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      </div>
    </>
  );
}
